import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Trash, ArrowCounterClockwise, Warning } from '@phosphor-icons/react';
import { formatDate } from '../utils/dateFormat';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';


function DeletedJobCard({ jo, onRestore, onPermanentDelete }) {
  return (
    <Card data-testid={`deleted-job-${jo.jo_number}`} className="border-zinc-300 rounded-sm shadow-sm border-l-4 border-l-red-400">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-xl font-bold text-zinc-950">JO #{jo.jo_number}</h3>
              <Badge className="bg-red-50 text-red-700 border border-red-200">Deleted</Badge>
              {jo.status_before_delete && (
                <Badge className="bg-zinc-50 text-zinc-600 border border-zinc-200">
                  Was: {jo.status_before_delete.replace(/_/g, ' ')}
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
              {[
                ['Customer', jo.customer_name],
                ['Delivery Date', formatDate(jo.delivery_date) || jo.delivery_date],
                ['Deleted By', jo.deleted_by],
                ['Deleted On', formatDate(jo.deleted_at) || jo.deleted_at]
              ].map(([label, value]) => (
                <div key={label}>
                  <span className="text-zinc-500 font-bold uppercase text-xs">{label}:</span>
                  <p className="text-zinc-950">{value}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid={`restore-${jo.jo_number}`} onClick={() => onRestore(jo)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-11">
              <ArrowCounterClockwise size={20} weight="bold" className="mr-2" /> Restore
            </Button>
            <Button data-testid={`permanent-delete-${jo.jo_number}`} onClick={() => onPermanentDelete(jo)} variant="outline" className="h-11 border-red-300 text-red-600 hover:bg-red-50">
              <Trash size={20} weight="bold" className="mr-2" /> Permanent Delete
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RestoreDialog({ target, onClose, onConfirm, loading }) {
  if (!target) return null;
  return (
    <Dialog open={!!target} onOpenChange={onClose}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowCounterClockwise size={24} weight="bold" className="text-emerald-600" />
            Restore Job Order #{target.jo_number}
          </DialogTitle>
          <DialogDescription className="sr-only">Confirm restoration of this job order</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <p className="text-sm text-zinc-700">
            This will restore the job order to its previous status: <span className="font-bold capitalize">{target.status_before_delete?.replace(/_/g, ' ')}</span>
          </p>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button data-testid="confirm-restore-button" onClick={onConfirm} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {loading ? 'Restoring...' : 'Confirm Restore'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PermanentDeleteDialog({ target, onClose, onConfirm, loading }) {
  if (!target) return null;
  return (
    <Dialog open={!!target} onOpenChange={onClose}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Warning size={24} weight="bold" />
            Permanently Delete Job Order #{target.jo_number}
          </DialogTitle>
          <DialogDescription className="sr-only">Confirm permanent deletion</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="bg-red-50 border border-red-300 rounded-sm p-4">
            <p className="text-sm text-red-800 font-bold">
              This will permanently remove this job order and ALL related data (production stages, procurement approvals, dispatch records). This action is IRREVERSIBLE.
            </p>
          </div>
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500 font-bold">Job Order No:</span>
              <span className="text-zinc-950 font-bold">{target.jo_number}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500 font-bold">Customer:</span>
              <span className="text-zinc-950">{target.customer_name}</span>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button data-testid="confirm-permanent-delete-button" onClick={onConfirm} disabled={loading} className="bg-red-600 hover:bg-red-700 text-white">
              {loading ? 'Deleting...' : 'Permanently Delete'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeletedJobOrders() {
  const [jobOrders, setJobOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDeletedJobs = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/job-orders/deleted`);
      setJobOrders(data);
    } catch {
      toast.error('Failed to load deleted job orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDeletedJobs(); }, [fetchDeletedJobs]);

  const handleRestore = async () => {
    setActionLoading(true);
    try {
      await api.post(`/api/job-orders/${restoreTarget.jo_number}/restore`, {});
      toast.success(`Job Order ${restoreTarget.jo_number} restored`);
      setRestoreTarget(null);
      fetchDeletedJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore');
    } finally { setActionLoading(false); }
  };

  const handlePermanentDelete = async () => {
    setActionLoading(true);
    try {
      await api.delete(`/api/job-orders/${deleteTarget.jo_number}/permanent-delete`);
      toast.success(`Job Order ${deleteTarget.jo_number} permanently deleted`);
      setDeleteTarget(null);
      fetchDeletedJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to permanently delete');
    } finally { setActionLoading(false); }
  };

  if (loading) {
    return <Layout><div className="text-center py-12">Loading deleted job orders...</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div>
          <h1 className="text-4xl font-light tracking-tight text-zinc-950">Deleted Job Orders</h1>
          <p className="text-sm text-zinc-500 mt-2">View, restore, or permanently delete job orders</p>
        </div>

        {jobOrders.length === 0 ? (
          <Card className="border-zinc-300 rounded-sm shadow-sm">
            <CardContent className="p-12 text-center text-zinc-500">No deleted job orders</CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {jobOrders.map(jo => (
              <DeletedJobCard
                key={jo.jo_number}
                jo={jo}
                onRestore={setRestoreTarget}
                onPermanentDelete={setDeleteTarget}
              />
            ))}
          </div>
        )}
      </div>

      <RestoreDialog target={restoreTarget} onClose={() => setRestoreTarget(null)} onConfirm={handleRestore} loading={actionLoading} />
      <PermanentDeleteDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handlePermanentDelete} loading={actionLoading} />
    </Layout>
  );
}
